import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import IconButton from '@sdk/components/ui/buttons/IconButton';
import Table from '@sdk/components/ui/table/Table';
import TableHeader from '@sdk/components/ui/table/TableHeader';
import TableHeaderCell from '@sdk/components/ui/table/TableHeaderCell';
import TableRow from '@sdk/components/ui/table/TableRow';
import TableCell from '@sdk/components/ui/table/TableCell';
import useOrderedData from '@sdk/utils/hooks/useOrderedData';
import useAuth from '@sdk/authentication/useAuth';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import ScreenLoader from '@sdk/components/ui/ScreenLoader';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import getRealDate from '@sdk/utils/getRealDate';
import replaceArrayElement from '@sdk/utils/replaceArrayElement';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { DayPart } from '@sdk/dayParts/types/apiTypes';
import type { DayPartExtended, TimeBlockExtended } from '@sdk/dayParts/types';
import type { PickerOption } from '@sdk/components/forms/types';

import AddSmallSvg from '../assets/svg/UI/add-small.svg';
import HiddenGraySvg from '../assets/svg/Icons/24/hidden-gray.svg';

import type { DayPartsState } from './components/timeBlocks/useDayParts';
import SettingsSection from './components/SettingsSection';
import { extendNewTimeBlock } from './helpers/extendNewTimeBlock';
import { useUserOrgSettings } from './UserOrgSettingsContext';

const HeaderRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const CenteredRow = styled(Row)`
  justify-content: center;
`;

const DAY_PART_EDIT_OPTION = 'editDayPart';
const DAY_PART_REMOVE_OPTION = 'removeDayPart';

const TIME_BLOCK_EDIT_OPTION = 'editTimeBlock';
const TIME_BLOCK_REMOVE_OPTION = 'removeTimeBlock';

type Props = {
  addEditDayPart: (params: { dayPart?: DayPartExtended }) => Promise<DayPartExtended>;
  addEditTimeBlock: (params: { timeBlock?: TimeBlockExtended }) => Promise<TimeBlockExtended>;
  setTimeBlocksChanged: (hasChanged: boolean) => void;
} & Partial<DayPartsState>;
export const TimeBlocksScreen: React.FC<React.PropsWithChildren<Props>> = ({
  addEditDayPart,
  addEditTimeBlock,
  setTimeBlocksChanged,
  loadDayPartsError,
  dayParts,
  setDayParts,
}) => {
  const {
    isDescending: isDescendingDayParts,
    changeOrder: changeOrderDayParts,
    orderProp: orderPropDayParts,
    orderedData: orderedDayParts,
  } = useOrderedData(dayParts);

  const { currentUser } = useAuth();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { shiftTemplates: timeBlocks } = userOrgSettings;

  const dayPartsMenu: PickerOption[] = [
    {
      key: DAY_PART_EDIT_OPTION,
      value: DAY_PART_EDIT_OPTION,
      label: gettext('Edit day part'),
    },
    {
      key: DAY_PART_REMOVE_OPTION,
      value: DAY_PART_REMOVE_OPTION,
      label: gettext('Remove day part'),
    },
  ];

  const timeBlocksMenu: PickerOption[] = [
    {
      key: TIME_BLOCK_EDIT_OPTION,
      value: TIME_BLOCK_EDIT_OPTION,
      label: gettext('Edit time block'),
    },
    {
      key: TIME_BLOCK_REMOVE_OPTION,
      value: TIME_BLOCK_REMOVE_OPTION,
      label: gettext('Remove time block'),
    },
  ];

  const addDayPart = async (): Promise<void> => {
    try {
      const addedDayPart = await addEditDayPart({});
      const newDayPart = {
        ...addedDayPart,
        timeStart: formatTime(addedDayPart.dtstart, false, true),
        timeEnd: formatTime(addedDayPart.dtend, false, true),
      };

      setDayParts([...dayParts, newDayPart]);
      setTimeBlocksChanged(true);
    } catch {
      // user cancelled the add - nothing left for us to do.
    }
  };

  const addTimeBlock = async (): Promise<void> => {
    try {
      const addedTimeBlock = await addEditTimeBlock({});
      onUserOrgSettingsChange({
        shiftTemplates: [...timeBlocks, extendNewTimeBlock(addedTimeBlock as any)],
      });
    } catch {
      // user cancelled the add - nothing left for us to do.
    }
  };

  const dayPartOptionClicked = async (option: PickerOption, dayPart: DayPart): Promise<void> => {
    const dayPartToUpdateIndex = dayParts.findIndex((dayPartToSave) => dayPartToSave === dayPart);
    if (option.value === DAY_PART_EDIT_OPTION) {
      try {
        const modifiedDayPart = await addEditDayPart({
          dayPart: {
            ...dayPart,
            dtstart: getRealDate(`2019-01-01T${dayPart.timeStart}`),
            dtend: getRealDate(`2019-01-01T${dayPart.timeEnd}`),
          },
        });
        // Angular gives us updated data only in dtstart and dtend fields, therefore we modify timeStart and timeEnd here manually
        modifiedDayPart.timeStart = formatTime(modifiedDayPart.dtstart, false, true);
        modifiedDayPart.timeEnd = formatTime(modifiedDayPart.dtend, false, true);
        setDayParts(replaceArrayElement(dayParts, dayPartToUpdateIndex, modifiedDayPart));
        setTimeBlocksChanged(true);
      } catch {
        // user cancelled the edit - nothing left for us to do.
      }
    }

    if (option.value === DAY_PART_REMOVE_OPTION) {
      if (!dayPart.id) {
        const newDayParts = dayParts.filter((dayPartToSave) => dayPartToSave !== dayPart);
        setDayParts(newDayParts);
      } else {
        const modifiedDayPart = {
          ...dayPart,
          deleted: true,
        };
        setDayParts(replaceArrayElement(dayParts, dayPartToUpdateIndex, modifiedDayPart));
      }
      setTimeBlocksChanged(true);
    }
  };

  const timeBlockOptionClicked = async (
    option: PickerOption,
    timeBlock: TimeBlockExtended
  ): Promise<void> => {
    if (option.value === TIME_BLOCK_EDIT_OPTION) {
      try {
        await addEditTimeBlock({ timeBlock });
        timeBlocks.push();
      } catch (e) {
        console.info('error', e);
      }
    }

    if (option.value === TIME_BLOCK_REMOVE_OPTION) {
      const newTimeBlocks = timeBlocks.filter((block) => block !== timeBlock);
      onUserOrgSettingsChange({
        shiftTemplates: newTimeBlocks,
      });
    }
  };

  /**
   * This methods helps extract proper formatting of start and end hours of day part.
   */
  const getStartEnd = (dayPart: DayPart): string => {
    const { timeStart, timeEnd } = dayPart;
    const { usTimeFormat } = currentUser.settings;
    const start = formatTime(getRealDate(`2019-01-01T${timeStart}`), usTimeFormat);
    const end = formatTime(getRealDate(`2019-01-01T${timeEnd}`), usTimeFormat);

    return `${start} - ${end}`;
  };

  if (loadDayPartsError) {
    return <Text14 themeColor={ThemeColorType.danger}>{loadDayPartsError}</Text14>;
  }

  if (!orderedDayParts) {
    return <ScreenLoader />;
  }

  const hasDayParts = Boolean(dayParts.filter(({ deleted }) => !deleted).length);

  return (
    <Column>
      {currentUser.org.isPremium && (
        <>
          <HeaderRow>
            <Text16 semiBold themeColor={ThemeColorType.primary}>
              {gettext('Day parts')}
            </Text16>
            <Row>
              <IconButton
                buttonType={ButtonType.default}
                iconLeft={<AddSmallSvg />}
                onClick={addDayPart}>
                {gettext('Add day part')}
              </IconButton>
            </Row>
          </HeaderRow>
          <Row top={10} bottom={20}>
            <HorizontalSeparator />
          </Row>
          <SettingsSection pBottom={60}>
            {!hasDayParts && (
              <CenteredRow>
                <Text14 italic themeColor={ThemeColorType.gray}>
                  {gettext('You have no day parts yet.')}
                </Text14>
              </CenteredRow>
            )}
            {hasDayParts && (
              <Table>
                <TableHeader>
                  <TableHeaderCell
                    label={gettext('name')}
                    orderProp="name"
                    changeOrder={changeOrderDayParts}
                    isDescending={isDescendingDayParts}
                    currentOrderProp={orderPropDayParts}
                  />
                  <TableHeaderCell
                    label={gettext('start time')}
                    orderProp="timeStart"
                    changeOrder={changeOrderDayParts}
                    isDescending={isDescendingDayParts}
                    currentOrderProp={orderPropDayParts}
                  />
                  <TableHeaderCell label={gettext('options')} alignRight width={50} />
                </TableHeader>
                <tbody>
                  {orderedDayParts
                    .filter(({ deleted }) => !deleted)
                    .map((dayPart, index) => (
                      <TableRow key={dayPart.id || index}>
                        <TableCell>
                          <Text14 themeColor={ThemeColorType.gray}>{dayPart.name}</Text14>
                        </TableCell>
                        <TableCell>
                          <Text14 themeColor={ThemeColorType.gray}>{getStartEnd(dayPart)}</Text14>
                        </TableCell>
                        <TableCell alignRight>
                          <MoreOptionsMenu
                            options={dayPartsMenu}
                            onClick={(option: PickerOption): Promise<void> =>
                              dayPartOptionClicked(option, dayPart)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </tbody>
              </Table>
            )}
          </SettingsSection>
        </>
      )}
      <HeaderRow>
        <Text16 semiBold themeColor={ThemeColorType.primary}>
          {gettext('Time blocks')}
        </Text16>
        <Row>
          <IconButton
            buttonType={ButtonType.default}
            iconLeft={<AddSmallSvg />}
            onClick={addTimeBlock}>
            {gettext('Add time block')}
          </IconButton>
        </Row>
      </HeaderRow>
      <Row top={10} bottom={20}>
        <HorizontalSeparator />
      </Row>
      <SettingsSection pBottom={20}>
        {!timeBlocks.length && (
          <CenteredRow>
            <Text14 italic themeColor={ThemeColorType.gray}>
              {gettext('You have no time blocks yet.')}
            </Text14>
          </CenteredRow>
        )}
        {Boolean(timeBlocks.length) && (
          <Table>
            <TableHeader>
              <TableHeaderCell label={gettext('Time')} />
              <TableHeaderCell label={gettext('Duration')} />
              <TableHeaderCell label={gettext('Location')} />
              <TableHeaderCell label={gettext('Break')} />
              <TableHeaderCell label={gettext('Options')} alignRight />
            </TableHeader>

            <tbody>
              {(timeBlocks as TimeBlockExtended[]).map((timeBlock, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <HeaderRow>
                      <Text14 themeColor={ThemeColorType.gray}>{timeBlock.time}</Text14>
                      {timeBlock.openEnd && (
                        <Row pLeft={10}>
                          <HiddenGraySvg
                            data-tip={gettext('End time hidden from employee schedules')}
                          />
                        </Row>
                      )}
                    </HeaderRow>
                  </TableCell>
                  <TableCell>
                    <Text14 themeColor={ThemeColorType.gray}>{timeBlock.durationLabel}</Text14>
                  </TableCell>
                  <TableCell>
                    <Text14 themeColor={ThemeColorType.gray}>{timeBlock.locationLabel}</Text14>
                  </TableCell>
                  <TableCell>
                    <Text14 themeColor={ThemeColorType.gray}>{timeBlock.breakLabel}</Text14>
                  </TableCell>
                  <TableCell alignRight>
                    <MoreOptionsMenu
                      options={timeBlocksMenu}
                      onClick={(option: PickerOption): Promise<void> =>
                        timeBlockOptionClicked(option, timeBlock)
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </SettingsSection>
      <Tooltip attachToBody />
    </Column>
  );
};
